import type { RulebookDisplayedSnapshot, RulebookLevelRangeKind } from "@next-platform/client-common-core";
import { resolveClockTimeOfDayIndex, resolveMeterLevelIndex } from "@next-platform/client-common-core";
import resolveHudMeterValue from "@next-platform/client-common-core/rulebook/hud/resolveHudMeterValue";
import { boundedMeterFill, DEFAULT_BAR_MAX } from "@next-platform/client-common-core/rulebook/meterFill";
import { Icon, IconSize, IconVariants } from "@next-platform/client-common-spicy-ui";
import type { PublicMeterLevel, PublicMeterView, RulebookHudMeter } from "@next-platform/packages-ts-types";
import { MeterType } from "@next-platform/packages-ts-types";
import type { CSSProperties } from "react";
import React, { useMemo } from "react";

import RulebookMeterBar from "@/components/Rulebook/RulebookMeterBar";
import RulebookMeterIcon from "@/components/Rulebook/RulebookMeterIcon";
import Trans from "@/components/Trans/Trans";
import { useMeterColor } from "@/hooks";

import { HUD_BOX, HUD_CAPS, HUD_CARD, HUD_MUTED } from "./hudClassNames";
import RulebookCounterValue from "./RulebookCounterValue";
import RulebookDiceValue from "./RulebookDiceValue";
import RulebookLevelList from "./RulebookLevelList";
import RulebookMeterTells from "./RulebookMeterTells";

const EMPTY_LEVELS: PublicMeterLevel[] = [];
const DEFAULT_STORY_DAY = 1;

type LevelGroupKind = "levels" | "thresholds" | "tod" | "days";

/** Minutes of the day that count as night (moon icon): before 06:00 and from 20:00. */
const NIGHT_ENDS_MINUTE = 360;
const NIGHT_STARTS_MINUTE = 1200;

type LevelGroupHeadingProps = {
  kind: LevelGroupKind;
};

const LevelGroupHeading = ({ kind }: LevelGroupHeadingProps) => (
  <div className={`mb-0.5 ${HUD_CAPS}`}>
    {kind === "levels" ? <Trans i18nKey="chat:rulebook.hud.sheet.levels" defaults="Levels" /> : null}
    {kind === "thresholds" ? <Trans i18nKey="chat:rulebook.hud.sheet.thresholds" defaults="Thresholds" /> : null}
    {kind === "tod" ? <Trans i18nKey="chat:rulebook.hud.sheet.timeOfDay" defaults="Time of day" /> : null}
    {kind === "days" ? <Trans i18nKey="chat:rulebook.hud.sheet.days" defaults="Days" /> : null}
  </div>
);

type LevelGroupProps = {
  kind: LevelGroupKind;
  levels: PublicMeterLevel[];
  currentLevelIndex: number;
  revealedIndex?: number;
  rangeKind: RulebookLevelRangeKind;
  meterColor?: string;
  characterName?: string;
  showLevelNumber?: boolean;
  testId: string;
};

const LevelGroup = ({
  kind,
  levels,
  currentLevelIndex,
  revealedIndex,
  rangeKind,
  meterColor,
  characterName,
  showLevelNumber = false,
  testId,
}: LevelGroupProps) => {
  if (levels.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1">
      <LevelGroupHeading kind={kind} />
      <RulebookLevelList
        data-testid={testId}
        levels={levels}
        currentLevelIndex={currentLevelIndex}
        revealedIndex={revealedIndex}
        rangeKind={rangeKind}
        meterColor={meterColor}
        characterName={characterName}
        showLevelNumber={showLevelNumber}
      />
    </div>
  );
};

type ClockReadoutBoxProps = { meter: PublicMeterView; snapshot: RulebookDisplayedSnapshot };

/** "Tuesday 10 March · 15:30 / Day 1 of the story", with a sun by day and a moon at night. */
const ClockReadoutBox = ({ meter, snapshot }: ClockReadoutBoxProps) => {
  const clock = snapshot.meta.clock;
  const minuteOfDay = clock?.minute_of_day ?? NIGHT_ENDS_MINUTE;
  const isNight = minuteOfDay < NIGHT_ENDS_MINUTE || minuteOfDay >= NIGHT_STARTS_MINUTE;
  const dayValues = useMemo(() => ({ day: clock?.day ?? DEFAULT_STORY_DAY }), [clock?.day]);
  const meterColor = useMeterColor(meter.color);
  const iconStyle = useMemo<CSSProperties | undefined>(() => (meterColor ? { color: meterColor } : undefined), [meterColor]);
  return (
    <div data-testid="RulebookMeterPanelCard-Clock" className={`flex items-center gap-2.5 ${HUD_BOX}`}>
      <span className="inline-flex flex-none" style={iconStyle} data-testid={isNight ? "RulebookMeterPanelCard-Night" : "RulebookMeterPanelCard-Day"}>
        <Icon icon={isNight ? IconVariants.MOON : IconVariants.SUN} size={IconSize.MEDIUM} />
      </span>
      <div className="flex flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{clock?.label ?? ""}</span>
        <span className={`text-xs ${HUD_MUTED}`}>
          <Trans i18nKey="chat:rulebook.hud.sheet.dayOfStory" defaults={"Day {{day}} of the story"} values={dayValues} />
        </span>
      </div>
    </div>
  );
};

type PanelCardHeaderProps = {
  meter: PublicMeterView;
  snapshot: RulebookDisplayedSnapshot;
  value: number;
  characterName?: string;
};

const PanelCardHeader = ({ meter, snapshot, value, characterName }: PanelCardHeaderProps) => {
  const isClock = meter.type === MeterType.CLOCK;
  const isBounded = meter.type === MeterType.BAR || meter.type === MeterType.PIPS;
  const meterColor = useMeterColor(meter.color);

  return (
    <>
      <div className="flex items-center gap-2 text-[15px]">
        <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={meterColor} />
        <span className="font-semibold">{meter.label}</span>
        {isBounded ? (
          <span className="ml-auto font-semibold">
            {value}
            <span className={`font-normal ${HUD_MUTED}`}>{` / ${meter.max ?? DEFAULT_BAR_MAX}`}</span>
          </span>
        ) : null}
      </div>
      {isBounded ? <RulebookMeterBar size="panel" fill={boundedMeterFill(meter, value)} color={meterColor} /> : null}
      {meter.type === MeterType.DICE ? (
        <div className={HUD_BOX}>
          <RulebookDiceValue meter={meter} value={value} isLarge data-testid="RulebookMeterPanelCard-Roll" />
        </div>
      ) : null}
      {meter.type === MeterType.NUMBER ? <RulebookCounterValue meter={meter} value={value} characterName={characterName} /> : null}
      {isClock ? <ClockReadoutBox meter={meter} snapshot={snapshot} /> : null}
    </>
  );
};

/** Current level for bounded/counter/dice meters; -1 for clocks (their bands are listed separately) and unrolled dice. */
const resolveCurrentLevelIndex = ({ meter, value }: { meter: PublicMeterView; value: number }): number => {
  if (meter.type === MeterType.CLOCK || (meter.type === MeterType.DICE && value < 1)) {
    return -1;
  }
  return resolveMeterLevelIndex({ levels: meter.levels, value });
};

type RulebookMeterPanelCardProps = {
  meter: PublicMeterView;
  snapshot: RulebookDisplayedSnapshot;
  characterName?: string;
  "data-testid"?: string;
};

/** A die reveals only the current roll's level; an older snapshot's highest-roll index must not reveal below it. */
const resolveRevealedIndices = (meter: PublicMeterView, hudMeter?: RulebookHudMeter | null) => ({
  revealedIndex: meter.type === MeterType.DICE ? undefined : hudMeter?.revealed,
  revealedTodIndex: hudMeter?.revealed_tod,
});

const RulebookMeterPanelCard = ({
  meter,
  snapshot,
  characterName,
  "data-testid": testId = "RulebookMeterPanelCard",
}: RulebookMeterPanelCardProps) => {
  const hudMeter = snapshot.meta.meters?.[meter.id];
  const value = resolveHudMeterValue({ meter, hudMeter });
  const { revealedIndex, revealedTodIndex } = resolveRevealedIndices(meter, hudMeter);
  const isClock = meter.type === MeterType.CLOCK;
  const meterColor = useMeterColor(meter.color);

  const currentLevelIndex = resolveCurrentLevelIndex({ meter, value });

  const todLevels = meter.tod_levels ?? EMPTY_LEVELS;
  const dayLevels = meter.day_levels ?? EMPTY_LEVELS;
  const currentTodIndex = isClock ? resolveClockTimeOfDayIndex({ meter, clock: snapshot.meta.clock }) : -1;
  const clockDay = snapshot.meta.clock?.day;
  const currentDayIndex = clockDay === undefined ? -1 : resolveMeterLevelIndex({ levels: dayLevels, value: clockDay });

  return (
    <div data-testid={`${testId}-${meter.id}`} className={HUD_CARD}>
      <PanelCardHeader meter={meter} snapshot={snapshot} value={value} characterName={characterName} />
      <RulebookMeterTells meter={meter} characterName={characterName} />
      {!isClock ? (
        <LevelGroup
          kind={meter.type === MeterType.NUMBER && meter.display !== "level" ? "thresholds" : "levels"}
          showLevelNumber={meter.type === MeterType.NUMBER && meter.display === "level"}
          levels={meter.levels}
          currentLevelIndex={currentLevelIndex}
          revealedIndex={revealedIndex}
          rangeKind="bounds"
          meterColor={meterColor}
          characterName={characterName}
          testId={`${testId}-${meter.id}-Levels`}
        />
      ) : null}
      {isClock ? (
        <>
          <LevelGroup
            kind="tod"
            levels={todLevels}
            currentLevelIndex={currentTodIndex}
            revealedIndex={revealedTodIndex}
            rangeKind="tod"
            meterColor={meterColor}
            characterName={characterName}
            testId={`${testId}-${meter.id}-TimeOfDay`}
          />
          <LevelGroup
            kind="days"
            levels={dayLevels}
            currentLevelIndex={currentDayIndex}
            revealedIndex={revealedIndex}
            rangeKind="days"
            meterColor={meterColor}
            characterName={characterName}
            testId={`${testId}-${meter.id}-Days`}
          />
        </>
      ) : null}
    </div>
  );
};

export default React.memo(RulebookMeterPanelCard);
